// One coordinated layout for every SDK-injected floating element on a page:
// top/bottom sticky bars, left/right sticky rails, and the bottom-corner
// stack (corner slider, sticky video, a stuck in-content video).
//
// Before this existed each mount function positioned itself once, at mount
// time, from config alone - so on a real page (jobopting.com) a corner
// slider shifted past a right rail landed on top of the article's own
// video, two widgets defaulting to the same corner sat on top of each other,
// a 728px bar overflowed a 375px phone, and a 600px rail ran under a
// top/bottom bar on a short viewport. Positioning is a page-wide question
// (it depends on what ELSE is floating and where the publisher's content
// column is), so it's answered in one place and re-answered on every
// resize, rather than per widget.
//
// Rules, applied in this order on every reflow():
//   1. Bars fill the viewport width, shrinking (never growing) to fit it,
//      and reserve exactly their rendered height via <html> padding.
//   2. Rails sit in the gutter between the viewport edge and the content
//      column, vertically between the bars. They shrink to fit (down to
//      MIN_SCALE) and are hidden when the viewport is below
//      MIN_SIDE_RAIL_VIEWPORT or the gutter is too small - never overlapping
//      content or a bar.
//   3. Corner items stack upward from above the bottom bar, past any visible
//      rail on their side, in registration order - never on top of each
//      other. On desktop (>= MIN_SIDE_RAIL_VIEWPORT, with a detectable
//      content column) they must also fit in the gutter beside the content,
//      shrinking down to MIN_SCALE and otherwise staying hidden until a
//      resize makes room. Below that width there is no gutter to speak of,
//      so they overlay content (the standard mobile sticky-ad pattern) but
//      shrink to fit the viewport.
//
// Pure layout bookkeeping - it owns no widget, no network, no timer. Every
// entry is removed by its owner (or dropped automatically once its element
// leaves the DOM), and the window listeners exist only while there's
// something to lay out.

export const MIN_SIDE_RAIL_VIEWPORT = 1200; // matches ad-units.js's MIN_SIDE
export const FLOATING_WIDGET_Z_INDEX = 9990; // matches ad-units.js
const EDGE = 18; // corner items' own margin from the viewport edge
const GAP = 12; // between any two floating things, or a float and content
const RAIL_EDGE = 8; // rails sit a little tighter to the edge than corners
const BAR_GUTTER = 8; // breathing room either side of a shrunk bar creative
const MIN_SCALE = 0.5; // below this a creative stops being legible

// Scaling a display creative is done with a transform on the creative
// itself (DisplayRenderer's wrapper, rendered asynchronously into our
// container) - a stylesheet rule on the container's direct children, driven
// by one CSS variable, works no matter when the creative actually mounts.
const SCALED_CLASS = 'playbid-scaled';
const STYLE_ID = 'playbid-floating-layout-style';
const SCALE_CSS =
  `.${SCALED_CLASS}{overflow:hidden}` +
  `.${SCALED_CLASS}>*{flex-shrink:0;transform:scale(var(--playbid-scale,1));transform-origin:top left}` +
  `.${SCALED_CLASS}.playbid-scaled--center>*{transform-origin:top center}`;

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = SCALE_CSS;
  (document.head ?? document.documentElement).appendChild(style);
}

function px(value) {
  return `${Math.round(value)}px`;
}

export function createFloatingLayout({ getContentRect = () => null, getTopOffset = () => 0 } = {}) {
  const html = document.documentElement;
  const readPadding = (side) => {
    try {
      return parseFloat(getComputedStyle(html)[`padding${side}`]) || 0;
    } catch {
      return 0;
    }
  };
  // Read ONCE, so reserving space never clobbers the publisher's own padding
  // and releasing always restores it exactly (ported from ad-units.js's
  // pageInsets()).
  const origTop = readPadding('Top');
  const origBottom = readPadding('Bottom');

  const bars = [];
  const rails = [];
  const corners = [];
  let barsTouched = false;
  let listening = false;

  const onResize = () => reflow();

  function syncListeners() {
    const any = bars.length + rails.length + corners.length > 0;
    if (any && !listening) {
      window.addEventListener('resize', onResize);
      window.addEventListener('load', onResize);
      listening = true;
    } else if (!any && listening) {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
      listening = false;
    }
  }

  function prune(list) {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (!list[i].el.isConnected) list.splice(i, 1);
    }
  }

  function setScale(entry, scale) {
    entry.el.style.setProperty('--playbid-scale', String(scale));
  }

  function layoutBars(vw) {
    let topH = 0;
    let bottomH = 0;
    for (const bar of bars) {
      const scale = Math.min(1, Math.max(0, vw - BAR_GUTTER * 2) / bar.width);
      const height = Math.round(bar.height * scale);
      setScale(bar, scale);
      bar.el.style.height = px(height);
      if (bar.side === 'top') {
        const offset = getTopOffset();
        bar.el.style.top = px(offset);
        topH = Math.max(topH, offset + height);
      } else {
        bottomH = Math.max(bottomH, height);
      }
    }
    if (barsTouched) {
      html.style.paddingTop = `${origTop + topH}px`;
      html.style.paddingBottom = `${origBottom + bottomH}px`;
    }
    return { topH, bottomH };
  }

  function layoutRails(vw, vh, content, { topH, bottomH }) {
    const extent = { left: 0, right: 0 };
    for (const rail of rails) {
      const availH = vh - topH - bottomH - RAIL_EDGE * 2;
      let scale = Math.min(1, availH / rail.height);
      if (content) {
        const room =
          rail.side === 'left'
            ? content.left - RAIL_EDGE - GAP
            : vw - content.right - RAIL_EDGE - GAP;
        scale = Math.min(scale, room / rail.width);
      }
      const visible = vw >= MIN_SIDE_RAIL_VIEWPORT && scale >= MIN_SCALE;
      rail.el.style.display = visible ? 'block' : 'none';
      if (!visible) continue;
      const width = rail.width * scale;
      const height = rail.height * scale;
      setScale(rail, scale);
      Object.assign(rail.el.style, {
        width: px(width),
        height: px(height),
        top: px(topH + RAIL_EDGE + (availH - height) / 2),
      });
      extent[rail.side] = Math.max(extent[rail.side], width);
    }
    return extent;
  }

  function layoutCorners(vw, vh, content, { topH, bottomH }, railExtent) {
    const desktop = vw >= MIN_SIDE_RAIL_VIEWPORT && content;
    for (const side of ['left', 'right']) {
      const offset = railExtent[side] > 0 ? EDGE + railExtent[side] + GAP : EDGE;
      const room = desktop
        ? side === 'left'
          ? content.left - offset - GAP
          : vw - offset - content.right - GAP
        : vw - offset - EDGE;
      let bottom = EDGE + bottomH;
      for (const item of corners) {
        if (item.side !== side) continue;
        const scale = Math.min(1, Math.max(0, room) / item.width);
        const fits = desktop ? scale >= MIN_SCALE : scale > 0;
        const width = item.width * scale;
        item.el.style.width = px(width);
        item.el.style[side] = px(offset);
        item.el.style[side === 'left' ? 'right' : 'left'] = '';
        if (item.scaleContent) setScale(item, scale);
        const height = item.height ? item.height * scale : item.el.offsetHeight || (width * 9) / 16; // fluid video: measure, else 16:9
        const overflowsTop = bottom + height > vh - topH - EDGE;
        const visible = fits && !overflowsTop;
        item.el.style.display = visible ? '' : 'none';
        if (!visible) continue;
        item.el.style.bottom = px(bottom);
        bottom += height + GAP;
      }
    }
  }

  function reflow() {
    prune(bars);
    prune(rails);
    prune(corners);
    syncListeners();
    const vw = window.innerWidth || 1024;
    const vh = window.innerHeight || 768;
    const insets = layoutBars(vw);
    // Read after the bars have set <html> padding, since that padding can
    // reflow the publisher's own content column.
    const content = getContentRect();
    const railExtent = layoutRails(vw, vh, content, insets);
    layoutCorners(vw, vh, content, insets, railExtent);
  }

  function add(list, entry) {
    if (entry.scaleContent !== false) {
      ensureStyle();
      entry.el.classList.add(SCALED_CLASS);
    }
    list.push(entry);
    reflow();
    return () => remove(entry.el);
  }

  function remove(el) {
    for (const list of [bars, rails, corners]) {
      const index = list.findIndex((entry) => entry.el === el);
      if (index !== -1) list.splice(index, 1);
    }
    reflow();
  }

  return {
    // `width`/`height` are the creative's natural size, before any shrink.
    addBar({ el, side, width, height }) {
      barsTouched = true;
      el.classList.add('playbid-scaled--center');
      return add(bars, { el, side, width, height, scaleContent: true });
    },
    addRail({ el, side, width, height }) {
      return add(rails, { el, side, width, height, scaleContent: true });
    },
    // `height: null` means "measure it" (a fluid video player sizes its own
    // height from its width). `scaleContent: false` for anything that
    // resizes itself from its container's width instead of needing a
    // transform (video.js with `fluid: true`).
    addCorner({ el, side, width, height = null, scaleContent = true }) {
      return add(corners, { el, side, width, height, scaleContent });
    },
    remove,
    reflow,
  };
}
